import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, X } from 'lucide-react';
import { getInvestors } from '../api';
import { useMobile } from './Layout';
import { useTheme } from '../ThemeContext';

const statusMeta = {
  PENDING:                 { color: '#6b7280', bg: '#f3f4f6',  label: 'Pending' },
  KYC_IN_PROGRESS:         { color: '#2563eb', bg: '#eff6ff',  label: 'KYC' },
  SUITABILITY_IN_PROGRESS: { color: '#7c3aed', bg: '#f5f3ff',  label: 'Suitability' },
  DOCUMENTS_PENDING:       { color: '#d97706', bg: '#fffbeb',  label: 'Documents' },
  ACTIVE:                  { color: '#16a34a', bg: '#f0fdf4',  label: 'Active' },
  SUSPENDED:               { color: '#dc2626', bg: '#fef2f2',  label: 'Suspended' },
  REJECTED:                { color: '#dc2626', bg: '#fef2f2',  label: 'Rejected' },
};

const highlight = (text, query, textColor) => {
  if (!query || !text) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: '#fef08a', color: textColor, borderRadius: 2, padding: '0 1px' }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
};

const GlobalSearch = () => {
  const navigate      = useNavigate();
  const isMobile      = useMobile();
  const { theme }     = useTheme();
  const inputRef      = useRef(null);
  const listRef       = useRef(null);
  const [open,      setOpen]      = useState(false);
  const [query,     setQuery]     = useState('');
  const [investors, setInvestors] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);

  const C = {
    navy:      '#1D4ED8',
    bg:        theme.bgBadge,
    white:     theme.bgCard,
    border:    theme.border,
    text:      theme.textPrimary,
    textMd:    theme.textSecondary,
    textSm:    theme.textSecondary,
    textXs:    theme.textTertiary,
    green:     '#16a34a', greenBg: '#f0fdf4',
    red:       '#dc2626', redBg:   '#fef2f2',
    amber:     '#d97706', amberBg: '#fffbeb',
    blue:      '#2563eb', blueBg:  '#eff6ff',
    purple:    '#7c3aed', purpleBg:'#f5f3ff',
  };
  // Load investors once on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    getInvestors()
      .then(res => setInvestors(res.data || []))
      .catch(() => {});
  }, []);

  // Keyboard shortcut — Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(v => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Filter results
  const results = query.trim().length < 1 ? [] : investors.filter(inv => {
    const q = query.toLowerCase();
    return (
      inv.full_name?.toLowerCase().includes(q) ||
      inv.email?.toLowerCase().includes(q) ||
      inv.pseudo_id?.toLowerCase().includes(q) ||
      inv.nationality?.toLowerCase().includes(q)
    );
  }).slice(0, 8);

  const handleSelect = useCallback((id) => {
    setOpen(false);
    navigate(`/investors/${id}`);
  }, [navigate]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[activeIdx]) {
      handleSelect(results[activeIdx].id);
    }
  };

  useEffect(() => { setActiveIdx(0); }, [query]);

  if (!open) {
    return (
      <button
      onClick={() => setOpen(true)}
      title="Search investors (⌘K)"
      style={{
        width: 280, padding: '8px 14px', borderRadius: 8,
        background: 'transparent',
        border: `1px solid ${C.border}`,
        color: C.textSm, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 13, fontWeight: 400, transition: 'all 0.15s',
        boxSizing: 'border-box',
      }}
      onMouseEnter={e => e.currentTarget.style.borderColor = '#3B82F6'}
      onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Search size={13} color={C.textSm} />
        <span style={{ color: C.textSm }}>Search investors…</span>
      </div>
      <kbd style={{
        background: C.bg, border: `1px solid ${C.border}`,
        borderRadius: 4, padding: '1px 6px',
        fontSize: 9, color: C.textXs, fontFamily: 'monospace',
      }}>
        ⌘K
      </kbd>
    </button>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => setOpen(false)}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(15,23,42,0.4)',
          zIndex: 900,
          backdropFilter: 'blur(2px)',
        }}
      />

      {/* Palette */}
      <div style={{
        position: 'fixed',
        top: isMobile ? 0 : '18%',
        left: isMobile ? 0 : '50%',
        right: isMobile ? 0 : 'auto',
        bottom: isMobile ? 'auto' : 'auto',
        transform: isMobile ? 'none' : 'translateX(-50%)',
        width: isMobile ? '100%' : '100%',
        maxWidth: isMobile ? '100%' : 560,
        background: C.white,
        borderRadius: isMobile ? '0 0 14px 14px' : 14,
        boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
        zIndex: 901, overflow: 'hidden',
      }}>

        {/* Search input */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 18px',
          borderBottom: query && results.length > 0 ? `1px solid ${C.border}` : 'none',
        }}>
          <Search size={16} color={C.textSm} style={{ flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search investors by name, email, or ID…"
            style={{
              flex: 1, border: 'none', outline: 'none',
              fontSize: 15, color: C.text, background: 'transparent',
            }}
          />
          {query && (
            <button onClick={() => setQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textXs, padding: 2 }}>
              <X size={14} />
            </button>
          )}
          <kbd style={{
            background: '#f1f5f9', border: `1px solid ${C.border}`,
            borderRadius: 4, padding: '2px 6px',
            fontSize: 10, color: C.textXs, fontFamily: 'monospace', flexShrink: 0,
          }}>
            ESC
          </kbd>
        </div>

        {/* Results */}
        {query.trim().length > 0 && (
         <div ref={listRef} style={{ maxHeight: isMobile ? '60vh' : 380, overflowY: 'auto' }}>
            {results.length === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center' }}>
                <p style={{ fontSize: 13, color: C.textSm, margin: 0 }}>
                  No investors found for <strong>"{query}"</strong>
                </p>
              </div>
            ) : (
              <>
                <div style={{ padding: '8px 18px 4px', fontSize: 10, fontWeight: 700, color: C.textXs, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {results.length} result{results.length !== 1 ? 's' : ''}
                </div>
                {results.map((inv, i) => {
                  const status = statusMeta[inv.onboarding_status] || { color: C.textSm, bg: '#f3f4f6', label: inv.onboarding_status };
                  const isActive = i === activeIdx;
                  return (
                    <div
                      key={inv.id}
                      onClick={() => handleSelect(inv.id)}
                      onMouseEnter={() => setActiveIdx(i)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '11px 18px', cursor: 'pointer',
                        background: isActive ? '#f0f4ff' : C.white,
                        borderLeft: isActive ? `3px solid ${C.navy}` : '3px solid transparent',
                        transition: 'background 0.1s',
                      }}
                    >
                      {/* Avatar */}
                      <div style={{
                        width: 34, height: 34, borderRadius: '50%',
                        background: isActive ? C.navy : '#e2e8f0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700,
                        color: isActive ? C.white : C.textMd,
                        flexShrink: 0, transition: 'all 0.1s',
                      }}>
                        {inv.full_name ? inv.full_name.charAt(0).toUpperCase() : '?'}
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>
                          {highlight(inv.full_name || 'Name erased', query)}
                        </p>
                        <p style={{ margin: '2px 0 0', fontSize: 11, color: C.textSm, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {highlight(inv.email || '', query)}
                          {inv.nationality && <span style={{ marginLeft: 6, color: C.textXs }}>· {inv.nationality}</span>}
                        </p>
                      </div>

                      {/* Status */}
                      <span style={{
                        background: status.bg, color: status.color,
                        padding: '2px 9px', borderRadius: 99,
                        fontSize: 10, fontWeight: 700, flexShrink: 0,
                      }}>
                        {status.label}
                      </span>

                      {/* Arrow */}
                      <span style={{ fontSize: 12, color: C.textXs, flexShrink: 0 }}>→</span>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {/* Empty state hint */}
        {query.trim().length === 0 && (
          <div style={{ padding: '20px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <User size={14} color={C.textXs} />
            <p style={{ margin: 0, fontSize: 12, color: C.textXs }}>
              Start typing to search across all investors…
            </p>
          </div>
        )}

       {/* Footer — desktop only */}
       {!isMobile && (
          <div style={{
            padding: '8px 18px',
            borderTop: `1px solid ${C.border}`,
            display: 'flex', gap: 16, alignItems: 'center',
          }}>
            {[
              { key: '↑↓', label: 'navigate' },
              { key: '↵',  label: 'open' },
              { key: 'ESC', label: 'close' },
            ].map(k => (
              <span key={k.key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <kbd style={{
                  background: '#f1f5f9', border: `1px solid ${C.border}`,
                  borderRadius: 4, padding: '1px 5px',
                  fontSize: 9, color: C.textXs, fontFamily: 'monospace',
                }}>
                  {k.key}
                </kbd>
                <span style={{ fontSize: 10, color: C.textXs }}>{k.label}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default GlobalSearch;